import { TransactionOrder } from "creso-core/types";
import { convertToCurrency, formatAsCrore, isSIPOrder } from "creso-core/utils";
import { format } from "date-fns";
import { ChevronRight } from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "web-design-system";

export function OrderUpdates({
  order,
  mandates,
}: {
  order: TransactionOrder;
  mandates?: Array<{
    id: number;
    bank_account_details?: {
      bank_name?: string;
      account_number?: string;
    };
    mandate_limit?: number;
    status?: string;
  }>;
}) {
  if (!isSIPOrder(order)) return null;

  const oldDetails = order.old_plan_details;

  if (order.action_type !== "MODIFY") return null;
  if (!oldDetails) return null;

  const changes: Array<{
    label: string;
    oldValue: string;
    newValue: string;
  }> = [];

  // Check start_date change (only compare the day, not month/year)
  if (
    oldDetails.start_date &&
    "start_date" in order &&
    order.start_date &&
    "frequency" in order &&
    order.frequency
  ) {
    try {
      const oldDateObj = new Date(oldDetails.start_date);
      const newDateObj = new Date(order.start_date as string);

      let dateChanged = false;
      let oldDayDisplay = "";
      let newDayDisplay = "";

      if (order.frequency === "WEEKLY") {
        // For weekly, compare day of week
        const oldDayOfWeek = oldDateObj.getDay();
        const newDayOfWeek = newDateObj.getDay();
        if (oldDayOfWeek !== newDayOfWeek) {
          dateChanged = true;
          oldDayDisplay = format(oldDateObj, "EEE");
          newDayDisplay = format(newDateObj, "EEE");
        }
      } else if (order.frequency === "MONTHLY") {
        // For monthly, compare day of month
        const oldDayOfMonth = oldDateObj.getDate();
        const newDayOfMonth = newDateObj.getDate();
        if (oldDayOfMonth !== newDayOfMonth) {
          dateChanged = true;
          oldDayDisplay = format(oldDateObj, "do");
          newDayDisplay = format(newDateObj, "do");
        }
      }

      if (dateChanged) {
        changes.push({
          label: order.frequency === "WEEKLY" ? "Day" : "Date",
          oldValue: oldDayDisplay,
          newValue: newDayDisplay,
        });
      }
    } catch {
      // If date parsing fails, skip date comparison
    }
  }

  // Check mandate change
  if (
    oldDetails.mandate_id &&
    "mandate_id" in order &&
    order.mandate_id &&
    oldDetails.mandate_id !== order.mandate_id &&
    mandates
  ) {
    const oldMandate = mandates.find((m) => m.id === oldDetails.mandate_id);
    const newMandate = mandates.find((m) => m.id === order.mandate_id);

    if (oldMandate && newMandate) {
      const formatMandate = (mandate: typeof oldMandate) => {
        if (mandate.bank_account_details?.bank_name) {
          let result = mandate.bank_account_details.bank_name
            ?.replace(/\bbank\b/gi, "")
            .trim();

          if (mandate.bank_account_details.account_number) {
            result += ` *${mandate.bank_account_details.account_number.slice(-3)}`;
          }

          if (
            mandate.mandate_limit !== undefined &&
            mandate.mandate_limit !== null
          ) {
            result += ` · ${formatAsCrore(mandate.mandate_limit.toString(), 0)}`;
          }

          return result;
        }
        return `Mandate #${mandate.id}`;
      };

      changes.push({
        label: "Mandate",
        oldValue: formatMandate(oldMandate),
        newValue: formatMandate(newMandate),
      });
    }
  }

  // Check amount change
  if (
    oldDetails.amount &&
    order.amount &&
    convertToCurrency(oldDetails.amount) !== convertToCurrency(order.amount)
  ) {
    changes.push({
      label: "Amount",
      oldValue: convertToCurrency(oldDetails.amount),
      newValue: convertToCurrency(order.amount),
    });
  }

  // Check step-up change
  const formatStepUpValue = (
    amount?: number | null,
    percentage?: number | null
  ) => {
    if (!amount && !percentage) return "Not set";
    return amount ? convertToCurrency(amount.toString()) : `${percentage}%`;
  };

  const formatStepUpFrequency = (frequency?: number | null) =>
    frequency ? `${frequency} months` : "Not set";

  if ("step_up_amount" in order || "step_up_percentage" in order) {
    const oldStepUpValue = formatStepUpValue(
      oldDetails.step_up_amount,
      oldDetails.step_up_percentage
    );
    const newStepUpValue = formatStepUpValue(
      order.step_up_amount,
      order.step_up_percentage
    );

    if (oldStepUpValue !== newStepUpValue) {
      changes.push({
        label: "Step-up",
        oldValue: oldStepUpValue,
        newValue: newStepUpValue,
      });
    }

    const oldStepUpFrequency = formatStepUpFrequency(
      oldDetails.step_up_frequency
    );
    const newStepUpFrequency = formatStepUpFrequency(order.step_up_frequency);

    if (oldStepUpFrequency !== newStepUpFrequency) {
      changes.push({
        label: "Step-up frequency",
        oldValue: oldStepUpFrequency,
        newValue: newStepUpFrequency,
      });
    }
  }

  if (changes.length === 0) return null;

  return (
    <Collapsible className="w-full mt-2 group">
      <CollapsibleTrigger
        onClick={(e) => e.stopPropagation()}
        className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground/80 transition-colors"
      >
        <ChevronRight className="h-3 w-3 transition-transform group-data-[state=open]:rotate-90" />
        View changes
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-3 space-y-2 pl-4">
        {changes.map((change, index) => (
          <div key={index} className="flex items-start gap-2 text-sm flex-wrap">
            <span className="text-xs text-muted-foreground">
              {change.label}:
            </span>
            <div className="flex items-center gap-2 flex-1 flex-wrap">
              <span className="text-xs text-muted-foreground break-words">
                {change.oldValue}
              </span>
              <span className="text-xs text-muted-foreground">→</span>
              <span className="text-xs text-muted-foreground font-medium break-words">
                {change.newValue}
              </span>
            </div>
          </div>
        ))}
      </CollapsibleContent>
    </Collapsible>
  );
}
